'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { DatadogAppRouter } from '@datadog/browser-rum-nextjs';
import { getUserOrganization } from '@/lib/agentApi';
import { logger } from '@/lib/logger';
import { identifyRumUser, reportOrganizationLookupFailure, type RumOrganization } from '@/lib/datadog/rum';

/**
 * Mounted once inside Providers (needs SessionProvider). Keeps the RUM user in
 * step with the NextAuth session and hosts the SDK's App Router view tracker.
 */
export function DatadogRum() {
  const { data: session } = useSession();
  const email = session?.user?.email ?? null;

  // The org is stored WITH the email it was resolved for, and read back only on
  // a match. Holding it as a bare value would need a reset when the session
  // changes, and until that reset ran the previous user's organization would be
  // attached to the new one's session.
  const [resolved, setResolved] = useState<{ email: string; org: RumOrganization | null } | null>(null);
  const org = resolved?.email === email ? resolved.org : null;

  // Identify on the email alone first. Events emitted before a user is set are
  // attributed to nobody, so waiting for the org round-trip would leave the
  // first seconds of every session anonymous; the org is attached a moment
  // later by re-identifying the same user.
  useEffect(() => {
    identifyRumUser(email, org);
  }, [email, org]);

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    getUserOrganization()
      .then((fetched) => { if (!cancelled) setResolved({ email, org: fetched }); })
      // Best-effort: an unreachable lookup leaves the session tagged with the
      // user rather than breaking the page it renders in — but visibly, since a
      // session with no firm drops out of every firm-level query.
      .catch((error: unknown) => {
        logger.warn('organization lookup failed; session is tagged with the user only', error);
        reportOrganizationLookupFailure(error);
        if (!cancelled) setResolved({ email, org: null });
      });
    return () => { cancelled = true; };
  }, [email]);

  return <DatadogAppRouter />;
}
